import { Check, ArrowRight } from "lucide-react";
import { PACKAGES } from "@/data/content";
import { StarDash } from "@/components/StarDash";

const Pricing = () => {
  const scrollContact = () =>
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 sm:py-32 bg-[#f1e7d7] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <div className="text-center reveal">
          <StarDash />
          <div className="mt-6 font-sans-body text-[11px] font-bold uppercase tracking-[0.32em] text-[#6b5a4a]">
            ★ Packages
          </div>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#2b1a14] leading-[0.95]">
            Honest pricing.
            <br />
            <span className="font-italic-serif text-[#c63027]">No surprises.</span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-[#5a4636]">
            Flat-rate pricing for 2 cleaners × 2 hours — vacuum and detergents
            included. Need more hours, different scope, or a commercial
            contract? Just ask.
          </p>
        </div>

        <div
          className="mt-12 sm:mt-16 grid lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8"
          data-testid="pricing-grid"
        >
          {PACKAGES.map((pkg, i) => {
            const highlighted = pkg.highlight;
            return (
              <div
                key={pkg.name}
                data-testid={`pricing-card-${pkg.name.toLowerCase()}`}
                className={`relative reveal flex flex-col p-6 sm:p-9 ${
                  highlighted
                    ? "bg-[#c63027] text-[#fbf3e6] shadow-xl lg:-translate-y-3"
                    : "bg-[#fbf3e6] text-[#2b1a14] border border-[#d9c9b2]"
                }`}
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                {/* double-frame inner outline (decorative) */}
                <div
                  className={`absolute inset-3 pointer-events-none border ${
                    highlighted ? "border-[#fbf3e6]/30" : "border-[#c63027]/25"
                  }`}
                />

                <div className="relative">
                  {highlighted && (
                    <div className="inline-block bg-[#fbf3e6] text-[#c63027] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.28em] font-sans-body mb-4">
                      ★ Most Picked ★
                    </div>
                  )}
                  <div
                    className={`font-sans-body text-[10px] font-bold uppercase tracking-[0.28em] ${
                      highlighted ? "text-[#fbf3e6]/85" : "text-[#6b5a4a]"
                    }`}
                  >
                    {pkg.forWhom}
                  </div>
                  <h3 className="font-display mt-3 text-5xl font-bold tracking-tight">
                    {pkg.name}
                  </h3>

                  <div
                    className={`mt-6 pb-6 border-b ${
                      highlighted ? "border-[#fbf3e6]/30" : "border-[#c63027]/20"
                    }`}
                  >
                    <div className="flex items-baseline gap-2">
                      <div className="font-display text-5xl font-bold tracking-tight">
                        {pkg.priceLabel}
                      </div>
                      {pkg.priceSuffix && (
                        <div
                          className={`font-italic-serif text-base ${
                            highlighted ? "opacity-85" : "text-[#6b5a4a]"
                          }`}
                        >
                          {pkg.priceSuffix}
                        </div>
                      )}
                    </div>
                    <div
                      className={`mt-2 font-sans-body text-[10px] uppercase tracking-[0.28em] ${
                        highlighted ? "opacity-85" : "text-[#6b5a4a]"
                      }`}
                    >
                      ★ {pkg.duration}
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3 flex-1">
                    {pkg.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-base">
                        <Check
                          className={`size-4 mt-1 shrink-0 ${
                            highlighted ? "text-[#fbf3e6]" : "text-[#c63027]"
                          }`}
                        />
                        <span
                          className={
                            highlighted ? "text-[#fbf3e6]/95" : "text-[#3b2a1f]"
                          }
                        >
                          {b}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={scrollContact}
                    data-testid={`pricing-cta-${pkg.name.toLowerCase()}`}
                    className={`pill-btn mt-9 inline-flex items-center justify-center gap-2 h-12 px-6 w-full transition-colors ${
                      highlighted
                        ? "bg-[#fbf3e6] text-[#c63027] hover:bg-white"
                        : "bg-[#c63027] text-[#fbf3e6] hover:bg-[#a8231b]"
                    }`}
                  >
                    Request quote
                    <ArrowRight className="size-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* First-time customer promo */}
        <div
          className="relative mt-16 sm:mt-20 max-w-4xl mx-auto bg-[#fbf3e6] p-7 sm:p-14 text-center reveal"
          data-testid="promo-card"
        >
          <div className="absolute inset-3 pointer-events-none border border-[#c63027]" />
          <div className="absolute inset-1 pointer-events-none border border-[#c63027]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-[#c63027] text-[#fbf3e6] px-4 sm:px-5 py-2 font-sans-body text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] sm:tracking-[0.32em]">
              <span>★</span>
              Limited Time
              <span>★</span>
            </div>
            <h3 className="font-display mt-6 text-3xl sm:text-5xl font-bold text-[#2b1a14]">
              First-Time Customer?
            </h3>
            <div className="font-display mt-5 text-6xl sm:text-8xl font-bold text-[#c63027] leading-none">
              10<span className="text-4xl sm:text-6xl align-top">%</span>
            </div>
            <p className="mt-4 text-base sm:text-lg text-[#5a4636]">
              off your first regular cleaning booking
            </p>
            <button
              onClick={scrollContact}
              data-testid="promo-book-btn"
              className="pill-btn mt-7 inline-flex items-center gap-3 h-13 px-7 sm:h-14 sm:px-9 py-3.5 bg-[#c63027] hover:bg-[#a8231b] text-[#fbf3e6] text-base sm:text-lg transition-colors shadow"
            >
              Book Now <ArrowRight className="size-5" />
            </button>
            <div className="mt-5 font-sans-body text-xs uppercase tracking-[0.28em] text-[#6b5a4a]">
              ★ 019-731 8121
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
